import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, message, Tree, Space, Upload, Row, Col, Typography } from 'antd';
import { DeleteOutlined, FolderOutlined, FileOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

message.config({
    duration: 5,        // Show 5 seconds
    maxCount: 3,        // Max 3 messages
    rtl: false,         // LTR layout
    prefixCls: 'ant-message', // Ensure CSS loads
});

const { DirectoryTree } = Tree;
const { Text } = Typography;

export default function Folders() {
    const navigate = useNavigate();
    const [currentPath, setCurrentPath] = useState('/uploads');
    const [files, setFiles] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [uploading, setUploading] = useState(false);

    // delete  code for modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteCode, setDeleteCode] = useState('');
    const DELETE_CODE = process.env.REACT_APP_DELETE_CODE || 'DEEPAK1234';

    // Add folder states
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    // Add zip upload state
    const [uploadingZip, setUploadingZip] = useState(false);

    // Load files/folders
    const loadFiles = async (path = '/uploads') => {
        try {
            const res = await api.get(`/folders/list?path=${encodeURIComponent(path)}`);
            setFiles(res.data.files || []);
            setTreeData(res.data.tree || []);
            setCurrentPath(path);
        } catch (error) {
            message.error('Failed to load files');
        }
    };

    // Tree navigation
// Update loadFiles call in treeSelect
    const onTreeSelect = (keys, info) => {
        if (info.node.path) {
            loadFiles(info.node.path.replace('/uploads', ''));
        }
    };

    useEffect(() => {
        loadFiles('');  // Empty = root /uploads
    }, []);

    // Actions
    const handleUpload = async ({ file }) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', currentPath);

        try {
            await api.post('/folders/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            message.success('Upload complete!');
            loadFiles(currentPath);
        } catch {
            message.error('Upload failed');
        }
        setUploading(false);
        return false;
    };

    const handleDownload = async (filePath) => {
        window.open(`/folders/download?path=${encodeURIComponent(filePath)}`, '_blank');
    };

    const handleRename = () => {
        api.post('/folders/rename', {
            oldPath: selectedItem.path,
            newName: renameValue
        }).then(() => {
            message.success('Renamed!');
            setShowRenameModal(false);
            setRenameValue('');
            loadFiles(currentPath);
        }).catch(() => message.error('Rename failed'));
    };

    const handleDelete = () => {
        if (deleteCode !== DELETE_CODE) return;

        api.post('/folders/delete', { path: selectedItem.path })
            .then(() => {
                message.success('Deleted!');
                setShowDeleteModal(false);
                setDeleteCode('');  // ✅ Clear code
                loadFiles(currentPath);
            })
            .catch(() => message.error('Delete failed'));
    };

// Create folder handler
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            message.error('Folder name required');
            return;
        }

        try {
            await api.post('/folders/create-folder', {
                path: currentPath || '/',
                name: newFolderName.trim()
            });
            message.success('Folder created!');

            setShowCreateFolderModal(false);
            setNewFolderName('');
            loadFiles(currentPath);

        } catch (error) {
            // 🔥 MULTIPLE DEBUG + FORCE MESSAGE
            const errorMsg = error.response?.data?.error || 'Failed to create folder';
            // Debug
            //console.log('🚨 SHOWING ERROR:', errorMsg);
            alert('Error: ' + errorMsg);
        }
    };






// ZIP folder upload handler
    const handleZipUpload = async ({ file }) => {
        setUploadingZip(true);

        const formData = new FormData();
        formData.append('zip', file);
        formData.append('path', currentPath || '');

        try {
            await api.post('/folders/upload-zip', formData);
            message.success('Folder uploaded and extracted!');
            loadFiles(currentPath);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'ZIP upload failed';
            message.error(errorMsg);
        } finally {
            setUploadingZip(false);
        }

        return false; // Prevent default upload
    };




    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => (
                <Space>
                    {record.isDir ? <FolderOutlined /> : <FileOutlined />}
                    <span>{record.name}</span>
                </Space>
            ),
        },
        { title: 'Size', dataIndex: 'size', key: 'size' },
        { title: 'Modified', dataIndex: 'modified', key: 'modified' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {!record.isDir && (
                        <Button
                            type="link"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(record.path)}
                        >
                            Download
                        </Button>
                    )}
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelectedItem(record);
                            setRenameValue(record.name);
                            setShowRenameModal(true);
                        }}
                    >
                        Rename
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            setSelectedItem(record);
                            setShowDeleteModal(true);
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Row gutter={16}>
                <Col span={6}>
                    <DirectoryTree
                        treeData={treeData}
                        onSelect={onTreeSelect}
                        defaultExpandAll
                    />
                </Col>
                <Col span={18}>
                    <Space style={{ marginBottom: 16 }}>
                        <span><b>Current Location:</b> {currentPath || '/'} ></span>
                        <Button
                            type="primary"
                            onClick={() => setShowCreateFolderModal(true)}
                            icon={<FolderOutlined />}
                        >
                            Create Folder
                        </Button>
                        <Upload
                            customRequest={handleZipUpload}
                            showUploadList={false}
                            accept=".zip"
                        >
                            <Button icon={<UploadOutlined />}>
                                Upload Folder (ZIPPED)
                            </Button>
                        </Upload>

                        <Upload
                            customRequest={handleUpload}
                            showUploadList={false}
                            accept=".pdf,image/*,.doc,.docx"
                            multiple
                        >
                            <Button icon={<UploadOutlined />}>
                                Upload Files
                            </Button>
                        </Upload>
                        <Button
                            onClick={() => loadFiles('/')}
                            type="default"
                        >
                            Home
                        </Button>
                    </Space>

                    <Table
                        dataSource={files}
                        columns={columns}
                        rowKey="path"
                        size="small"
                        loading={uploading || uploadingZip}
                        pagination={{ pageSize: 50 }}
                    />
                </Col>
            </Row>

            {/* Rename Modal */}
            <Modal
                title="Rename"
                open={showRenameModal}
                onOk={handleRename}
                onCancel={() => setShowRenameModal(false)}
            >
                <Input value={renameValue} onChange={e => setRenameValue(e.target.value)} />
            </Modal>

            {/* Delete Modal */}
            <Modal
                title="🔒 Confirm Delete"
                open={showDeleteModal}
                onCancel={() => {
                    setShowDeleteModal(false);
                    setDeleteCode('');
                }}
                footer={null}
                width={400}
            >
                {selectedItem && (
                    <>
                        <p><strong>Delete "{selectedItem.name}"?</strong></p>
                        <p style={{ color: '#666', fontSize: 14 }}>
                            Path: <Text code>{selectedItem.path}</Text>
                            {selectedItem.isDir && <span style={{ color: 'orange' }}> (Folder - Recursive)</span>}
                        </p>
                        <div style={{ margin: '16px 0' }}>
                            <Input.Password
                                placeholder="Enter delete code to confirm"
                                value={deleteCode}
                                onChange={(e) => setDeleteCode(e.target.value)}
                                onPressEnter={handleDelete}
                                size="large"
                            />
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <Button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteCode('');
                                }}
                                style={{ marginRight: 8 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                danger
                                disabled={deleteCode !== DELETE_CODE}
                                onClick={handleDelete}
                            >
                                Delete {selectedItem.isDir ? 'Folder' : 'File'}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            {/* Create Folder Modal */}
            <Modal
                title="Create New Folder"
                open={showCreateFolderModal}
                onOk={handleCreateFolder}
                onCancel={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                }}
                okButtonProps={{
                    disabled: !newFolderName.trim()
                }}
                okText="Create"
            >
                <Input
                    placeholder="Enter folder name (no / or \\)"
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    maxLength={100}
                    status={newFolderName.includes('/') ? 'error' : ''}
                />
                {newFolderName.trim() && (
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
                        Will create: {currentPath ? `${currentPath.replace(/\/$/, '')}/${newFolderName.trim()}` : newFolderName.trim()}
                    </Text>
                )}
            </Modal>




        </div>
    );
}
